import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  "https://f0usr04663.execute-api.us-east-1.amazonaws.com";

export type AccessRole = {
  accessRoleId: string;
  name: string;
  description: string;
  integratorUserId: string;
  integratorName: string;
  cameraIds: string[];
  cameraCount: number;
  userIds: string[];
  userCount: number;
  createdAt: string | null;
  updatedAt: string | null;
};

export type AccessRoleListResponse = {
  items: AccessRole[];
  callerRole: "OWNER" | "INTEGRATOR" | "ADMIN" | "VIEWER";
  count: number;
};

async function idToken(): Promise<string> {
  const tokens = await cognitoUserPoolsTokenProvider.getTokens();
  const token = tokens?.idToken?.toString();
  if (!token) {
    throw new Error("Your session has expired. Sign out and sign in again.");
  }
  return token;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await idToken();
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  });
  const text = await response.text();
  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }
  if (!response.ok) {
    throw new Error(
      payload?.message ||
        payload?.error ||
        text ||
        `Request failed with status ${response.status}`,
    );
  }
  return payload as T;
}

export function listAccessRoles(): Promise<AccessRoleListResponse> {
  return request<AccessRoleListResponse>("/access-roles");
}

export async function createAccessRole(input: {
  name: string;
  description?: string;
}): Promise<AccessRole> {
  const payload = await request<{ role: AccessRole }>("/access-roles", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return payload.role;
}

export async function updateAccessRole(
  accessRoleId: string,
  input: { name?: string; description?: string },
): Promise<AccessRole> {
  const payload = await request<{ role: AccessRole }>(
    `/access-roles/${encodeURIComponent(accessRoleId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
  return payload.role;
}

export function deleteAccessRole(
  accessRoleId: string,
): Promise<{ deleted: boolean; accessRoleId: string }> {
  return request(`/access-roles/${encodeURIComponent(accessRoleId)}`, {
    method: "DELETE",
  });
}

export async function setAccessRoleCameras(
  accessRoleId: string,
  cameraIds: string[],
): Promise<AccessRole> {
  const payload = await request<{ role: AccessRole }>(
    `/access-roles/${encodeURIComponent(accessRoleId)}/cameras`,
    {
      method: "PUT",
      body: JSON.stringify({ cameraIds }),
    },
  );
  return payload.role;
}
